import { api, ApiError } from '../api/client';
import { uploadBlob } from '../api/blobUpload';
import {
  decryptAesGcm,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  unpackEncryptedBlob,
} from './primitives';

export const FOLDER_MANIFEST_AAD = 'shareweb:manifest:v1';

export interface SharedFolderActorRef {
  kind: 'user' | 'guest';
  id: string;
  displayName: string;
}

export interface SharedFolderDirectoryEntry {
  type: 'directory';
  path: string;
  entryId: string;
  createdAt: string;
  createdBy?: SharedFolderActorRef;
}

export interface SharedFolderFileEntry {
  type: 'file';
  path: string;
  fileId: string;
  blobId: string;
  contentHash: string;
  size: number;
  chunkPlaintextBytes: number;
  chunkCount: number;
  chunkCiphertextSizes?: number[];
  wrappedContentKey: string;
  contentType?: string;
  createdAt?: string;
  createdBy?: SharedFolderActorRef;
  updatedAt: string;
  updatedBy?: SharedFolderActorRef;
}

export type SharedFolderManifestEntry = SharedFolderDirectoryEntry | SharedFolderFileEntry;

export interface SharedFolderManifestPlaintext {
  entries: SharedFolderManifestEntry[];
}

export interface SharedFolderDirectoryTreeNode {
  path: string;
  name: string;
  entryId: string;
  createdAt: string;
  createdBy?: SharedFolderActorRef;
  children: SharedFolderDirectoryTreeNode[];
}

export function createEmptyManifest(): SharedFolderManifestPlaintext {
  return { entries: [] };
}

export async function encryptFolderManifest(
  folderKey: Uint8Array,
  manifest: SharedFolderManifestPlaintext,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(folderKey);
  const plaintext = new TextEncoder().encode(JSON.stringify(manifest));
  const encrypted = await encryptAesGcm(aesKey, plaintext, new TextEncoder().encode(FOLDER_MANIFEST_AAD));
  return packEncryptedBlob(encrypted);
}

export async function decryptFolderManifest(
  folderKey: Uint8Array,
  blob: Uint8Array,
): Promise<SharedFolderManifestPlaintext> {
  const aesKey = await importAesKey(folderKey);
  const decrypted = await decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(blob),
    new TextEncoder().encode(FOLDER_MANIFEST_AAD),
  );
  return JSON.parse(new TextDecoder().decode(decrypted)) as SharedFolderManifestPlaintext;
}

export async function loadSharedFolderManifest(
  folderId: string,
  folderKey: Uint8Array,
): Promise<{
  manifest: SharedFolderManifestPlaintext;
  revision: number;
  manifestBlobId: string;
}> {
  const meta = await api.getSharedFolderManifestMeta(folderId);
  const blob = await api.fetchSharedFolderManifest(folderId);
  const manifest = await decryptFolderManifest(folderKey, blob);
  return {
    manifest,
    revision: meta.revision,
    manifestBlobId: meta.manifestBlobId,
  };
}

export async function commitSharedFolderManifest(
  folderId: string,
  folderKey: Uint8Array,
  expectedRevision: number,
  manifest: SharedFolderManifestPlaintext,
  fileBlobRefs?: Array<{ fileId: string; blobId: string }>,
): Promise<number> {
  const encrypted = await encryptFolderManifest(folderKey, manifest);
  const manifestBlobId = await uploadBlob(encrypted);
  const committed = await api.commitSharedFolderManifest(folderId, {
    expectedRevision,
    manifestBlobId,
    fileBlobRefs,
  });
  return committed.revision;
}

export interface ManifestPathConflict {
  originalPath: string;
  resolvedPath: string;
}

export interface ManifestCommitResult {
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  conflicts: ManifestPathConflict[];
}

export function buildConflictPath(path: string, shortId = crypto.randomUUID().slice(0, 8)): string {
  const slashIndex = path.lastIndexOf('/');
  if (slashIndex === -1) {
    return appendConflictSuffix(path, shortId);
  }

  const directory = path.slice(0, slashIndex);
  const name = path.slice(slashIndex + 1);
  return `${directory}/${appendConflictSuffix(name, shortId)}`;
}

function appendConflictSuffix(name: string, shortId: string): string {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex > 0) {
    return `${name.slice(0, dotIndex)} (conflict ${shortId})${name.slice(dotIndex)}`;
  }

  return `${name} (conflict ${shortId})`;
}

function pathName(path: string): string {
  const slashIndex = path.lastIndexOf('/');
  return slashIndex === -1 ? path : path.slice(slashIndex + 1);
}

function parentPath(path: string): string {
  const slashIndex = path.lastIndexOf('/');
  return slashIndex === -1 ? '' : path.slice(0, slashIndex);
}

export function joinManifestPath(parent: string, name: string): string {
  const trimmed = name.trim();
  return parent ? `${parent}/${trimmed}` : trimmed;
}

export function parentManifestPath(path: string): string {
  return parentPath(path);
}

export function isManifestPathTaken(
  manifest: SharedFolderManifestPlaintext,
  path: string,
  ignorePath?: string,
): boolean {
  return manifest.entries.some((entry) => {
    if (ignorePath && (entry.path === ignorePath || entry.path.startsWith(`${ignorePath}/`))) {
      return false;
    }
    return entry.path === path;
  });
}

export function resolvePathForMerge(
  manifest: SharedFolderManifestPlaintext,
  desiredPath: string,
  shortId?: string,
): { path: string; conflict: boolean } {
  const occupied = new Set(manifest.entries.map((entry) => entry.path));
  if (!occupied.has(desiredPath)) {
    return { path: desiredPath, conflict: false };
  }

  let candidate = buildConflictPath(desiredPath, shortId);
  while (occupied.has(candidate)) {
    candidate = buildConflictPath(desiredPath);
  }

  return { path: candidate, conflict: true };
}

export function mergeManifestEntries(
  latest: SharedFolderManifestPlaintext,
  intendedNewEntries: SharedFolderManifestEntry[],
): { manifest: SharedFolderManifestPlaintext; conflicts: ManifestPathConflict[] } {
  const conflicts: ManifestPathConflict[] = [];
  const entries = [...latest.entries];
  const occupied = new Set(entries.map((entry) => entry.path));

  for (const entry of intendedNewEntries) {
    let path = entry.path;
    if (occupied.has(path)) {
      const resolved = resolvePathForMerge({ entries }, path);
      path = resolved.path;
      if (resolved.conflict) {
        conflicts.push({ originalPath: entry.path, resolvedPath: path });
      }
    }

    const merged = { ...entry, path };
    entries.push(merged);
    occupied.add(path);
  }

  return { manifest: { entries }, conflicts };
}

export type ManifestFileBlobRef = { fileId: string; blobId: string };
export type ManifestFileBlobRefs =
  | ManifestFileBlobRef[]
  | ((manifest: SharedFolderManifestPlaintext) => ManifestFileBlobRef[]);

export function fileBlobRefsFromManifest(
  manifest: SharedFolderManifestPlaintext,
): ManifestFileBlobRef[] {
  return manifest.entries
    .filter((entry): entry is SharedFolderFileEntry => entry.type === 'file')
    .map((entry) => ({ fileId: entry.fileId, blobId: entry.blobId }));
}

export function replaceFileContentsInManifest(
  manifest: SharedFolderManifestPlaintext,
  fileId: string,
  patch: Partial<SharedFolderFileEntry> & Pick<
    SharedFolderFileEntry,
    'blobId' | 'contentHash' | 'size' | 'chunkPlaintextBytes' | 'chunkCount' | 'wrappedContentKey' | 'updatedAt'
  >,
): SharedFolderManifestPlaintext {
  const index = manifest.entries.findIndex((entry) => entry.type === 'file' && entry.fileId === fileId);
  if (index < 0) {
    throw new Error('file_deleted');
  }

  const existing = manifest.entries[index] as SharedFolderFileEntry;
  const entries = [...manifest.entries];
  entries[index] = { ...existing, ...patch };
  return { entries };
}

function resolveManifestFileBlobRefs(
  spec: ManifestFileBlobRefs | undefined,
  manifest: SharedFolderManifestPlaintext,
): ManifestFileBlobRef[] {
  if (spec === undefined) {
    return fileBlobRefsFromManifest(manifest);
  }

  return typeof spec === 'function' ? spec(manifest) : spec;
}

export async function appendManifestEntries(
  folderId: string,
  folderKey: Uint8Array,
  currentRevision: number,
  currentManifest: SharedFolderManifestPlaintext,
  intendedNewEntries: SharedFolderManifestEntry[],
  fileBlobRefs?: ManifestFileBlobRefs,
): Promise<ManifestCommitResult> {
  let revision = currentRevision;
  let baseManifest = currentManifest;
  const maxAttempts = 3;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { manifest, conflicts } = mergeManifestEntries(baseManifest, intendedNewEntries);
    try {
      const newRevision = await commitSharedFolderManifest(
        folderId,
        folderKey,
        revision,
        manifest,
        resolveManifestFileBlobRefs(fileBlobRefs, manifest),
      );
      return { revision: newRevision, manifest, conflicts };
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409 || attempt === maxAttempts - 1) {
        throw error;
      }

      const latest = await loadSharedFolderManifest(folderId, folderKey);
      revision = latest.revision;
      baseManifest = latest.manifest;
    }
  }

  throw new Error('manifest_commit_failed');
}

export async function commitManifestMutation(
  folderId: string,
  folderKey: Uint8Array,
  currentRevision: number,
  currentManifest: SharedFolderManifestPlaintext,
  mutate: (latest: SharedFolderManifestPlaintext) => SharedFolderManifestPlaintext,
  fileBlobRefs?: ManifestFileBlobRefs,
): Promise<ManifestCommitResult> {
  let revision = currentRevision;
  let baseManifest = currentManifest;
  const maxAttempts = 3;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const manifest = mutate(baseManifest);
    try {
      const newRevision = await commitSharedFolderManifest(
        folderId,
        folderKey,
        revision,
        manifest,
        resolveManifestFileBlobRefs(fileBlobRefs, manifest),
      );
      return { revision: newRevision, manifest, conflicts: [] };
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409 || attempt === maxAttempts - 1) {
        throw error;
      }

      const latest = await loadSharedFolderManifest(folderId, folderKey);
      revision = latest.revision;
      baseManifest = latest.manifest;
    }
  }

  throw new Error('manifest_commit_failed');
}

export function countDirectoryDescendants(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
): { files: number; directories: number } {
  let files = 0;
  let directories = 0;
  const childPrefix = `${directoryPath}/`;

  for (const entry of manifest.entries) {
    if (entry.path === directoryPath || !entry.path.startsWith(childPrefix)) {
      continue;
    }

    if (entry.type === 'file') {
      files += 1;
    } else {
      directories += 1;
    }
  }

  return { files, directories };
}

export function isDirectoryEmpty(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
): boolean {
  const childPrefix = `${directoryPath}/`;
  return !manifest.entries.some((entry) => entry.path.startsWith(childPrefix));
}

export function deleteDirectoryFromManifest(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
): SharedFolderManifestPlaintext {
  return {
    entries: manifest.entries.filter((entry) => entry.path !== directoryPath),
  };
}

export function deleteDirectoryRecursiveFromManifest(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
): SharedFolderManifestPlaintext {
  const childPrefix = `${directoryPath}/`;
  return {
    entries: manifest.entries.filter(
      (entry) => entry.path !== directoryPath && !entry.path.startsWith(childPrefix),
    ),
  };
}

export function deleteFileFromManifest(
  manifest: SharedFolderManifestPlaintext,
  filePath: string,
): SharedFolderManifestPlaintext {
  return {
    entries: manifest.entries.filter((entry) => entry.path !== filePath),
  };
}

export function renameDirectoryInManifest(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
  newName: string,
): { manifest: SharedFolderManifestPlaintext; conflicts: ManifestPathConflict[] } {
  const trimmed = newName.trim();
  if (!trimmed || trimmed.includes('/') || trimmed.includes('\\')) {
    throw new Error('invalid_folder_name');
  }

  const parent = directoryPath.includes('/')
    ? directoryPath.slice(0, directoryPath.lastIndexOf('/'))
    : '';
  const newPath = parent ? `${parent}/${trimmed}` : trimmed;
  if (newPath === directoryPath) {
    return { manifest, conflicts: [] };
  }

  const unrelatedEntries = manifest.entries.filter(
    (entry) => entry.path !== directoryPath && !entry.path.startsWith(`${directoryPath}/`),
  );
  if (unrelatedEntries.some((entry) => entry.path === newPath)) {
    throw new Error('name_already_in_use');
  }

  const entries = manifest.entries.map((entry) => {
    if (entry.path === directoryPath) {
      return { ...entry, path: newPath };
    }

    if (entry.path.startsWith(`${directoryPath}/`)) {
      const suffix = entry.path.slice(directoryPath.length);
      return { ...entry, path: `${newPath}${suffix}` };
    }

    return entry;
  });

  return { manifest: { entries }, conflicts: [] };
}

export function moveDirectoryInManifest(
  manifest: SharedFolderManifestPlaintext,
  directoryPath: string,
  destinationDirectoryPath: string,
): { manifest: SharedFolderManifestPlaintext; conflicts: ManifestPathConflict[] } {
  if (!directoryPath) {
    throw new Error('cannot_move_root_directory');
  }

  const targetParent = destinationDirectoryPath.trim().replace(/^\/+|\/+$/g, '');
  if (directoryPath === targetParent || targetParent.startsWith(`${directoryPath}/`)) {
    throw new Error('invalid_move_destination');
  }

  const currentParent = parentPath(directoryPath);
  const desiredPath = targetParent ? `${targetParent}/${pathName(directoryPath)}` : pathName(directoryPath);
  if (desiredPath === directoryPath || currentParent === targetParent) {
    return { manifest, conflicts: [] };
  }

  const conflicts: ManifestPathConflict[] = [];
  const unrelatedEntries = manifest.entries.filter(
    (entry) => entry.path !== directoryPath && !entry.path.startsWith(`${directoryPath}/`),
  );
  let resolvedNewPath = desiredPath;
  if (unrelatedEntries.some((entry) => entry.path === desiredPath)) {
    const resolved = resolvePathForMerge({ entries: unrelatedEntries }, desiredPath);
    resolvedNewPath = resolved.path;
    if (resolved.conflict) {
      conflicts.push({ originalPath: desiredPath, resolvedPath: resolvedNewPath });
    }
  }

  const entries = manifest.entries.map((entry) => {
    if (entry.path === directoryPath) {
      return { ...entry, path: resolvedNewPath };
    }

    if (entry.path.startsWith(`${directoryPath}/`)) {
      const suffix = entry.path.slice(directoryPath.length);
      return { ...entry, path: `${resolvedNewPath}${suffix}` };
    }

    return entry;
  });

  return { manifest: { entries }, conflicts };
}

export function renameFileInManifest(
  manifest: SharedFolderManifestPlaintext,
  filePath: string,
  newName: string,
): { manifest: SharedFolderManifestPlaintext; conflicts: ManifestPathConflict[] } {
  const trimmed = newName.trim();
  if (!trimmed || trimmed.includes('/') || trimmed.includes('\\')) {
    throw new Error('invalid_file_name');
  }

  const slashIndex = filePath.lastIndexOf('/');
  const parent = slashIndex === -1 ? '' : filePath.slice(0, slashIndex);
  const newPath = parent ? `${parent}/${trimmed}` : trimmed;
  if (newPath === filePath) {
    return { manifest, conflicts: [] };
  }

  const unrelatedEntries = manifest.entries.filter((entry) => entry.path !== filePath);
  if (unrelatedEntries.some((entry) => entry.path === newPath)) {
    throw new Error('name_already_in_use');
  }

  const entries = manifest.entries.map((entry) => {
    if (entry.type === 'file' && entry.path === filePath) {
      return { ...entry, path: newPath, updatedAt: new Date().toISOString() };
    }

    return entry;
  });

  return { manifest: { entries }, conflicts: [] };
}

export function moveFileInManifest(
  manifest: SharedFolderManifestPlaintext,
  filePath: string,
  destinationDirectoryPath: string,
): { manifest: SharedFolderManifestPlaintext; conflicts: ManifestPathConflict[] } {
  const targetParent = destinationDirectoryPath.trim().replace(/^\/+|\/+$/g, '');
  const desiredPath = targetParent ? `${targetParent}/${pathName(filePath)}` : pathName(filePath);
  if (desiredPath === filePath || parentPath(filePath) === targetParent) {
    return { manifest, conflicts: [] };
  }

  const conflicts: ManifestPathConflict[] = [];
  const unrelatedEntries = manifest.entries.filter((entry) => entry.path !== filePath);
  let resolvedNewPath = desiredPath;
  if (unrelatedEntries.some((entry) => entry.path === desiredPath)) {
    const resolved = resolvePathForMerge({ entries: unrelatedEntries }, desiredPath);
    resolvedNewPath = resolved.path;
    if (resolved.conflict) {
      conflicts.push({ originalPath: desiredPath, resolvedPath: resolvedNewPath });
    }
  }

  const entries = manifest.entries.map((entry) => {
    if (entry.type === 'file' && entry.path === filePath) {
      return { ...entry, path: resolvedNewPath, updatedAt: new Date().toISOString() };
    }

    return entry;
  });

  return { manifest: { entries }, conflicts };
}

export function buildDirectoryTree(
  manifest: SharedFolderManifestPlaintext,
): SharedFolderDirectoryTreeNode {
  const root: SharedFolderDirectoryTreeNode = {
    path: '',
    name: '/',
    entryId: 'root',
    createdAt: '',
    children: [],
  };
  const nodes = new Map<string, SharedFolderDirectoryTreeNode>([['', root]]);

  const ensureNode = (path: string): SharedFolderDirectoryTreeNode => {
    const existing = nodes.get(path);
    if (existing) {
      return existing;
    }

    const parent = ensureNode(parentPath(path));
    const explicit = manifest.entries.find(
      (entry) => entry.type === 'directory' && entry.path === path,
    );
    const node: SharedFolderDirectoryTreeNode = {
      path,
      name: pathName(path),
      entryId: explicit?.type === 'directory' ? explicit.entryId : path,
      createdAt: explicit?.type === 'directory' ? explicit.createdAt : '',
      createdBy: explicit?.type === 'directory' ? explicit.createdBy : undefined,
      children: [],
    };
    nodes.set(path, node);
    parent.children.push(node);
    parent.children.sort((left, right) => left.name.localeCompare(right.name));
    return node;
  };

  for (const entry of manifest.entries) {
    const fullPath = entry.type === 'directory' ? entry.path : parentPath(entry.path);
    if (!fullPath) {
      continue;
    }

    const segments = fullPath.split('/');
    let built = '';
    for (const segment of segments) {
      built = built ? `${built}/${segment}` : segment;
      ensureNode(built);
    }
  }

  return root;
}

export function listFolderContents(
  manifest: SharedFolderManifestPlaintext,
  currentPath: string,
): {
  directories: SharedFolderDirectoryEntry[];
  files: SharedFolderFileEntry[];
} {
  const prefix = currentPath ? `${currentPath}/` : '';
  const directories = new Map<string, SharedFolderDirectoryEntry>();
  const files: SharedFolderFileEntry[] = [];

  for (const entry of manifest.entries) {
    if (currentPath && !entry.path.startsWith(prefix)) {
      continue;
    }

    const relative = currentPath ? entry.path.slice(prefix.length) : entry.path;
    if (!relative) {
      continue;
    }

    const slashIndex = relative.indexOf('/');
    if (slashIndex === -1) {
      if (entry.type === 'directory') {
        directories.set(entry.path, entry);
      } else {
        files.push(entry);
      }
      continue;
    }

    const dirPath = currentPath
      ? `${currentPath}/${relative.slice(0, slashIndex)}`
      : relative.slice(0, slashIndex);
    if (!directories.has(dirPath)) {
      const existing = manifest.entries.find(
        (candidate) => candidate.type === 'directory' && candidate.path === dirPath,
      );
      directories.set(
        dirPath,
        existing?.type === 'directory'
          ? existing
          : {
              type: 'directory',
              path: dirPath,
              entryId: dirPath,
              createdAt: '',
            },
      );
    }
  }

  return {
    directories: [...directories.values()].sort((a, b) => a.path.localeCompare(b.path)),
    files: files.sort((a, b) => a.path.localeCompare(b.path)),
  };
}
